import React, { useEffect, useRef, useState } from 'react'
import { ChevronDownIcon } from '@heroicons/react/24/outline'
import classnames from 'classnames'
import type { TariffHistoryItem } from '../../../apis/types'
import { formatUTCDetailedDate } from '../../../util/string'
import { formatTariffSourceLabel, TariffLatestBadge, TariffStatusChip } from './TariffChips'

function formatHistoryDateRange(h: TariffHistoryItem): string {
    const start = formatUTCDetailedDate(new Date(h.effectiveDate))
    const end = h.endDate != null ? formatUTCDetailedDate(new Date(h.endDate)) : 'Current'
    return `${start} – ${end}`
}

const HistorySourceBadge: React.FC<{ source: TariffHistoryItem['source'] }> = ({ source }) => {
    if (source === 'UNKNOWN') return null
    const label = formatTariffSourceLabel(source)
    if (label === '') return null
    return (
        <span className="inline-flex shrink-0 rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            {label}
        </span>
    )
}

const HistoryOptionLabel: React.FC<{ history: TariffHistoryItem; isLatest: boolean }> = ({
    history,
    isLatest,
}) => (
    <span className="flex min-w-0 items-center gap-2">
        <span className="whitespace-nowrap">{formatHistoryDateRange(history)}</span>
        <TariffStatusChip status={history.status} />
        {isLatest ? <TariffLatestBadge /> : null}
        <HistorySourceBadge source={history.source} />
    </span>
)

export const TariffHistorySelect: React.FC<{
    histories: TariffHistoryItem[]
    selectedHistoryCuid: string | null | undefined
    latestCuid: string | undefined
    onSelect: (historyCuid: string) => void
}> = ({ histories, selectedHistoryCuid, latestCuid, onSelect }) => {
    const [open, setOpen] = useState(false)
    const rootRef = useRef<HTMLDivElement>(null)

    const selected = histories.find((h) => h.cuid === selectedHistoryCuid) ?? histories[0]

    useEffect(() => {
        if (!open) return
        const onDoc = (e: MouseEvent): void => {
            if (rootRef.current != null && !rootRef.current.contains(e.target as Node)) {
                setOpen(false)
            }
        }
        document.addEventListener('mousedown', onDoc)
        return () => document.removeEventListener('mousedown', onDoc)
    }, [open])

    if (selected == null) return null

    const selectedIsLatest = selected.cuid === latestCuid

    return (
        <div ref={rootRef} className="relative w-full min-w-[20rem] max-w-2xl">
            <button
                type="button"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-label="Select tariff rateset by effective date"
                onClick={() => setOpen((v) => !v)}
                className="inline-flex w-full min-w-0 items-center justify-between gap-2 rounded-md border border-slate-300 bg-white px-2.5 py-1.5 text-sm text-slate-900 shadow-sm hover:bg-slate-50 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:hover:bg-slate-800"
            >
                <HistoryOptionLabel history={selected} isLatest={selectedIsLatest} />
                <ChevronDownIcon className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
            </button>
            {open ? (
                <div
                    role="listbox"
                    className="absolute left-0 z-30 mt-1 max-h-60 min-w-full w-max max-w-2xl overflow-auto rounded-md border border-slate-200 bg-white py-1 shadow-lg dark:border-slate-700 dark:bg-slate-900"
                >
                    {histories.map((h) => {
                        const isSelected = h.cuid === selected.cuid
                        const isLatest = h.cuid === latestCuid
                        return (
                            <div key={h.cuid} role="option" aria-selected={isSelected}>
                                <button
                                    type="button"
                                    className={classnames(
                                        'flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-800',
                                        isSelected && 'bg-slate-50 font-medium dark:bg-slate-800'
                                    )}
                                    onClick={() => {
                                        setOpen(false)
                                        if (!isSelected) onSelect(h.cuid)
                                    }}
                                >
                                    <HistoryOptionLabel history={h} isLatest={isLatest} />
                                </button>
                            </div>
                        )
                    })}
                </div>
            ) : null}
        </div>
    )
}
